import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { format, startOfMonth } from "date-fns";
import { IconReload } from "@tabler/icons-react";

import { TimelogTable } from "./features/timelogTable";
import { getEmployee, getTimelogReport } from "./services/api";
import { setAccessToken } from "./services/api/client";
import { loginRequest } from "config/authentication/azure/authConfig";

const today = new Date();
const defaultStartDate = format(startOfMonth(today), "yyyy-MM-dd");
const defaultEndDate = format(today, "yyyy-MM-dd");

const TimelogReport = () => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [resetPagination, setResetPagination] = useState(false);

  const perPageRef = useRef(10);
  const searchTimerRef = useRef(null);

  const acquireToken = async () => {
    const { accessToken } = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    setAccessToken(accessToken);
  };

  const fetchData = async (start, end, skip, take, col, dir, search) => {
    setIsLoading(true);
    try {
      await acquireToken();
      const response = await getTimelogReport(start, end, skip, take, col, dir, search || null);
      setData(response.data.data);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      if (error.response) navigate(`/${error.response.status}`);
      else console.error("Error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // On mount: check admin, then load first page
  useEffect(() => {
    const init = async () => {
      try {
        await acquireToken();
        const userInfo = await getEmployee(accounts[0].username);
        if (!userInfo.data?.isAdmin) {
          navigate("/403");
          return;
        }
        await fetchData(defaultStartDate, defaultEndDate, 0, perPageRef.current, "DoorLog", "ASC", null);
      } catch (error) {
        if (error.response) navigate(`/${error.response.status}`);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = () => {
    setResetPagination((r) => !r);
    fetchData(startDate, endDate, 0, perPageRef.current, "DoorLog", "ASC", null);
  };

  const handlePageChange = (page) => {
    fetchData(startDate, endDate, (page - 1) * perPageRef.current, perPageRef.current, "DoorLog", "ASC", null);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    const take = newPerPage > 0 ? newPerPage : perPageRef.current;
    perPageRef.current = take;
    fetchData(startDate, endDate, (page - 1) * take, take, "DoorLog", "ASC", null);
  };

  const handleSort = (column, direction) => {
    setResetPagination((r) => !r);
    fetchData(startDate, endDate, 0, perPageRef.current, column.sortField, direction.toUpperCase(), null);
  };

  const handleSearchChange = (value) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setResetPagination((r) => !r);
      fetchData(startDate, endDate, 0, perPageRef.current, "DoorLog", "ASC", value || null);
    }, 500);
  };

  return (
    <>
      <div className="container-xl">
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col">
              <div className="page-pretitle">Admin</div>
              <h2 className="page-title">Employee Onsite Report</h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="d-flex gap-2 align-items-end">
                <div>
                  <label className="form-label mb-1">Start Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    max={endDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="form-label mb-1">End Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate}
                    min={startDate}
                    max={format(today, "yyyy-MM-dd")}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleFilter}
                  disabled={isLoading}
                >
                  Apply
                </button>
                <button
                  className="btn btn-white text-muted btn-sm"
                  onClick={handleFilter}
                  disabled={isLoading}
                >
                  <IconReload className="icon" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="container-xl">
          <TimelogTable
            data={data}
            totalCount={totalCount}
            isLoading={isLoading}
            onPageChange={handlePageChange}
            onPerRowsChange={handlePerRowsChange}
            onSort={handleSort}
            onSearchChange={handleSearchChange}
            resetPagination={resetPagination}
          />
        </div>
      </div>
    </>
  );
};

export default TimelogReport;
